import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'

const EXEMPT_PATHS = ['/terms', '/privacy', '/guidelines', '/cookie-policy']

export default function SuspensionGate({ children }) {
  const { user, getToken } = useAuth()
  const location = useLocation()
  const [status, setStatus] = useState('loading') // loading | clear | suspended
  const [reason, setReason] = useState('')
  const [expiresAt, setExpiresAt] = useState(null)
  const [remaining, setRemaining] = useState('')
  const timerRef = useRef(null)

  const isExempt = EXEMPT_PATHS.includes(location.pathname)

  useEffect(() => {
    if (isExempt || !user) { setStatus('clear'); return }

    let cancelled = false
    ;(async () => {
      try {
        const token = await getToken()
        if (!token) { setStatus('clear'); return } // session not yet populated; fail open
        const { data } = await axios.get('/api/user/suspension-status', {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (cancelled) return
        if (data.suspended) {
          setReason(data.reason)
          setExpiresAt(data.expiresAt ? new Date(data.expiresAt) : null)
          setStatus('suspended')
        } else {
          setStatus('clear')
        }
      } catch {
        if (!cancelled) setStatus('clear') // fail open
      }
    })()
    return () => { cancelled = true }
  }, [user, getToken])

  // Countdown timer
  useEffect(() => {
    if (status !== 'suspended' || !expiresAt) return

    function tick() {
      const now = Date.now()
      const end = expiresAt.getTime()
      const diff = end - now

      if (diff <= 0) {
        setStatus('clear')
        return
      }

      const days = Math.floor(diff / 86400000)
      const hours = Math.floor((diff % 86400000) / 3600000)
      const mins = Math.floor((diff % 3600000) / 60000)
      const secs = Math.floor((diff % 60000) / 1000)

      let parts = []
      if (days > 0) parts.push(`${days}d`)
      if (hours > 0) parts.push(`${hours}h`)
      if (mins > 0) parts.push(`${mins}m`)
      parts.push(`${secs}s`)
      setRemaining(parts.join(' '))
    }

    tick()
    timerRef.current = setInterval(tick, 1000)
    return () => clearInterval(timerRef.current)
  }, [status, expiresAt])

  if (status === 'loading' || status === 'clear') return children

  return (
    <div className="suspension-gate">
      <div className="suspension-gate__card">
        <div className="suspension-gate__icon">⏸</div>
        <h1 className="suspension-gate__title">Account Suspended</h1>
        <p className="suspension-gate__desc">
          Your account has been suspended{expiresAt ? ' temporarily' : ''} for violating our community guidelines.
        </p>
        <div className="suspension-gate__reason">
          <div className="suspension-gate__reason-label">Reason</div>
          <p className="suspension-gate__reason-text">{reason}</p>
        </div>
        {expiresAt && (
          <div className="suspension-gate__timer">
            <div className="suspension-gate__timer-label">Time remaining</div>
            <div className="suspension-gate__timer-value">{remaining}</div>
          </div>
        )}
        <p className="suspension-gate__note">
          You will regain access to all features once the suspension expires. If you believe this was a mistake, please contact{' '}
          <a href="mailto:support@versesite.com" className="suspension-gate__link">support@versesite.com</a>.
        </p>
        <div className="suspension-gate__legal">
          <a href="/terms">Terms of Service</a>
          <span> · </span>
          <a href="/guidelines">Community Guidelines</a>
        </div>
      </div>
    </div>
  )
}
